import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { DropdownMenu as M, Popover as P, Tooltip as T } from 'radix-ui';
import { CheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { floatingPanel, menuItem } from './overlay-styles';

// ── DropdownMenu ────────────────────────────────────────────────────────────
export const DropdownMenu = M.Root;
export const DropdownMenuTrigger = M.Trigger;
export const DropdownMenuGroup = M.Group;

export function DropdownMenuContent({
  className,
  align = 'end',
  sideOffset = 6,
  ...rest
}: ComponentPropsWithoutRef<typeof M.Content>) {
  return (
    <M.Portal>
      <M.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(floatingPanel, 'min-w-52 p-1', className)}
        {...rest}
      />
    </M.Portal>
  );
}

export function DropdownMenuItem({
  className,
  icon,
  shortcut,
  tone,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof M.Item> & {
  icon?: ReactNode;
  shortcut?: ReactNode;
  tone?: 'danger';
}) {
  return (
    <M.Item
      className={cn(
        menuItem,
        tone === 'danger' &&
          'text-negative-ink data-[highlighted]:bg-negative-soft data-[highlighted]:text-negative-ink [&_svg]:!text-negative-ink',
        className,
      )}
      {...rest}
    >
      {icon}
      <span className="flex-1">{children}</span>
      {shortcut && <span className="ml-4 text-xs text-ink-subtle">{shortcut}</span>}
    </M.Item>
  );
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof M.CheckboxItem>) {
  return (
    <M.CheckboxItem className={cn(menuItem, 'pl-8', className)} {...rest}>
      <M.ItemIndicator className="absolute left-2.5 inline-flex">
        <CheckIcon weight="bold" className="!text-primary-ink" />
      </M.ItemIndicator>
      {children}
    </M.CheckboxItem>
  );
}

export function DropdownMenuLabel({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof M.Label>) {
  return <M.Label className={cn('eyebrow px-2.5 pb-1 pt-2', className)} {...rest} />;
}

export function DropdownMenuSeparator({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof M.Separator>) {
  return <M.Separator className={cn('-mx-1 my-1 h-px bg-hairline', className)} {...rest} />;
}

export const DropdownMenuRadioGroup = M.RadioGroup;
export function DropdownMenuRadioItem({
  className,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof M.RadioItem>) {
  return (
    <M.RadioItem className={cn(menuItem, 'pl-8', className)} {...rest}>
      <M.ItemIndicator className="absolute left-3 inline-flex">
        <span className="size-1.5 rounded-full bg-primary" />
      </M.ItemIndicator>
      {children}
    </M.RadioItem>
  );
}

// ── Popover ─────────────────────────────────────────────────────────────────
export const Popover = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverClose = P.Close;
export const PopoverAnchor = P.Anchor;
export function PopoverContent({
  className,
  sideOffset = 8,
  align = 'center',
  ...rest
}: ComponentPropsWithoutRef<typeof P.Content>) {
  return (
    <P.Portal>
      <P.Content
        sideOffset={sideOffset}
        align={align}
        className={cn(floatingPanel, 'w-72 p-4 focus:outline-none', className)}
        {...rest}
      />
    </P.Portal>
  );
}

// ── Tooltip ─────────────────────────────────────────────────────────────────
export const TooltipProvider = T.Provider;

export function Tooltip({
  content,
  children,
  side = 'top',
  delay,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
}) {
  if (!content) return <>{children}</>;
  return (
    <T.Root delayDuration={delay}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          className="z-[60] max-w-64 rounded-sm bg-ink px-2.5 py-1.5 text-xs leading-snug text-ink-inverse shadow-md data-[state=delayed-open]:animate-[gh-pop_140ms_var(--gh-ease-out)]"
        >
          {content}
          <T.Arrow className="fill-ink" width={10} height={5} />
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}
